/**
 * useRunAllOrchestrator — M3 client-side Run-all chain.
 *
 * Sequential stages over the EXISTING endpoints the page already calls:
 *   author  → AuthorOnboardingDialog (pause-for-input; skip when author exists)
 *   pillars → confirm drafts via TopicPillarService, or (auto + none) author
 *             analyze scope='pillars' with autoConfirmPillars:true
 *   topics  → generateTopicsBatch → review pause (drafts ON) or auto-apply
 *   strategy→ createStrategy with generateFirstSprintPosts:true and
 *             firstSprintApplyMode per the drafts toggle
 *   posts   → chained first-sprint generation task → review pause or
 *             auto-applied posts
 *
 * Resumable per-stage: every stage re-checks D12 readiness counts before
 * running, so re-launching Run-all skips work that already exists. Review
 * checkpoints are explicit pause rows (D1 review-ON default). Stopping only
 * stops the CLIENT chain — already-dispatched backend tasks keep running.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import {
  CONTENT_PLAN_BACKLOG_READY_THRESHOLD,
  type ContentPlanReadinessCounts,
} from '@modules/shared-types';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { TopicPillarService } from '../../services/TopicPillarService';
import { ContentStrategyFirestoreService } from '../../services/ContentStrategyFirestoreService';
import { useContentStrategyTask } from '../../hooks/useContentStrategyTask';
import { useAuthorPlatforms } from '../../hooks/useAuthorPlatforms';
import { usePillars } from '../../hooks/usePillars';
import type { ContentMixPreset, PlatformId, PrimaryGoal } from '../../types';

export type RunAllStageId = 'author' | 'pillars' | 'topics' | 'strategy' | 'posts';

export type RunAllStageStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'skipped'
  | 'done'
  | 'error';

export type RunAllPauseKind =
  | 'needs-author'
  | 'confirm-pillars'
  | 'review-topics'
  | 'review-posts';

export interface RunAllStageState {
  id: RunAllStageId;
  label: string;
  status: RunAllStageStatus;
  /** Backend task the stage is waiting on (running state). */
  taskId?: string;
  /** Generation run id for review pauses (topics/posts). */
  generationId?: string;
  /** Sprint the chained post run targets (posts review apply). */
  sprintId?: string;
  pauseKind?: RunAllPauseKind;
  /** FULL error text (never truncated). */
  error?: string;
  note?: string;
}

export interface RunAllOptions {
  /** ON (default): pause at draft review checkpoints instead of auto-apply. */
  reviewDrafts: boolean;
  /** ON (default): NO pillar auto-confirm — explicit confirm checkpoint. */
  confirmPillarsManually: boolean;
}

export interface UseRunAllOrchestratorArgs {
  projectId: string;
  userId: string;
  /** Effective author (readiness hook resolution). Null on an empty project. */
  authorId: string | null;
  counts: ContentPlanReadinessCounts;
  /** Opens the page-owned AuthorOnboardingDialog (pause-for-input action). */
  onOpenOnboarding: () => void;
}

export interface UseRunAllOrchestratorReturn {
  active: boolean;
  options: RunAllOptions;
  stages: RunAllStageState[];
  /** Task the currently running stage waits on (drives the phase pill). */
  currentPhaseName?: string;
  start: (options: RunAllOptions) => void;
  stop: () => void;
  dismiss: () => void;
  /** Paused-stage actions surfaced by the panel rows. */
  confirmAllDraftPillars: () => Promise<void>;
  retryStage: (stageId: RunAllStageId) => void;
  /** Review-dialog wiring (topics/posts) — panel opens the existing dialogs. */
  reviewTarget: { entity: 'topic' | 'post'; generationId: string; sprintId?: string } | null;
  openReview: (stage: RunAllStageState) => void;
  closeReview: () => void;
  applyReview: (acceptedItemIds: string[]) => Promise<void>;
}

const STAGE_LABELS: Record<RunAllStageId, string> = {
  author: 'Author profile',
  pillars: 'Confirm pillars',
  topics: 'Generate topics',
  strategy: 'Create strategy',
  posts: 'First sprint posts',
};

const STAGE_ORDER: RunAllStageId[] = ['author', 'pillars', 'topics', 'strategy', 'posts'];

function initialStages(): RunAllStageState[] {
  return STAGE_ORDER.map((id) => ({ id, label: STAGE_LABELS[id], status: 'pending' }));
}

export function useRunAllOrchestrator({
  projectId,
  userId,
  authorId,
  counts,
  onOpenOnboarding,
}: UseRunAllOrchestratorArgs): UseRunAllOrchestratorReturn {
  const apiClient = useMemo(() => new ContentPlanApiClient(), []);
  const pillarService = useMemo(() => new TopicPillarService(), []);

  const [active, setActive] = useState(false);
  const [options, setOptions] = useState<RunAllOptions>({
    reviewDrafts: true,
    confirmPillarsManually: true,
  });
  const [stages, setStages] = useState<RunAllStageState[]>(initialStages);
  const [reviewTarget, setReviewTarget] = useState<
    { entity: 'topic' | 'post'; generationId: string; sprintId?: string } | null
  >(null);

  // Draft pillars for the confirm checkpoint (auto & manual paths).
  const { pillars } = usePillars(active ? projectId : null);
  const draftPillars = useMemo(
    () => pillars.filter((pillar) => pillar.isDraft
      && (!authorId || (Array.isArray(pillar.authorIds) && pillar.authorIds.includes(authorId)))),
    [pillars, authorId],
  );

  // Platforms feed the strategy-create defaults.
  const { platforms } = useAuthorPlatforms(active ? projectId : null, authorId);

  const runningTaskId = useMemo(
    () => stages.find((s) => s.status === 'running' && s.taskId)?.taskId ?? null,
    [stages],
  );
  const runningTask = useContentStrategyTask(active ? projectId : null, runningTaskId);

  const patchStage = useCallback((id: RunAllStageId, patch: Partial<RunAllStageState>) => {
    setStages((prev) => prev.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage)));
  }, []);

  // Guard against double-starting the same stage from effect re-entry.
  const startedStageRef = useRef<RunAllStageId | null>(null);

  // ── Stage launchers ────────────────────────────────────────────────

  const startTopicsGeneration = useCallback(async () => {
    patchStage('topics', { status: 'running', error: undefined, pauseKind: undefined });
    try {
      const missing = Math.max(
        CONTENT_PLAN_BACKLOG_READY_THRESHOLD - counts.readyTopicCount,
        0,
      );
      const response = await apiClient.generateTopicsBatch({
        brief: {
          id: `brief_runall_${Date.now().toString(36)}`,
          projectId,
          userId,
          authorId: authorId ?? undefined,
          count: Math.max(missing + 2, 12),
        },
        sourceData: {},
        authorId: authorId ?? undefined,
      });
      if (!response.success) throw new Error(response.error ?? 'Topic generation failed to start');
      patchStage('topics', { status: 'running', taskId: response.taskId, generationId: response.taskId });
    } catch (e) {
      patchStage('topics', { status: 'error', error: (e as Error).message });
    }
  }, [apiClient, authorId, counts.readyTopicCount, patchStage, projectId, userId]);

  const startPillarsAutoGeneration = useCallback(async () => {
    if (!authorId) {
      patchStage('pillars', {
        status: 'paused',
        pauseKind: 'confirm-pillars',
        note: 'No author resolved — finish the author step first.',
      });
      return;
    }
    patchStage('pillars', { status: 'running', error: undefined });
    try {
      const response = await apiClient.authorAnalyze({
        projectId,
        userId,
        existingAuthorId: authorId,
        scope: 'pillars',
        autoConfirmPillars: true,
      });
      if (!response.success || !response.taskId) {
        throw new Error(response.error ?? 'Pillar generation failed to start');
      }
      patchStage('pillars', { status: 'running', taskId: response.taskId });
    } catch (e) {
      patchStage('pillars', { status: 'error', error: (e as Error).message });
    }
  }, [apiClient, authorId, patchStage, projectId, userId]);

  const startStrategyCreation = useCallback(async () => {
    patchStage('strategy', { status: 'running', error: undefined });
    try {
      const platformIds = platforms
        .filter((platform) => platform.isActive !== false)
        .map((platform) => platform.id) as PlatformId[];
      if (platformIds.length === 0) {
        throw new Error('The author has no platforms — enable at least one on the Platforms sub-tab.');
      }
      const today = new Date();
      const end = new Date(today.getTime() + 28 * 86_400_000);
      const iso = (d: Date) => d.toISOString().slice(0, 10);
      const response = await apiClient.createStrategy({
        projectId,
        userId,
        authorId: authorId ?? '',
        name: `Run-all strategy ${iso(today)}`,
        dateStart: iso(today),
        dateEnd: iso(end),
        targetPlatforms: platformIds,
        postsPerWeekPerPlatform: Object.fromEntries(platformIds.map((p) => [p, 3])),
        primaryGoal: 'leads' as PrimaryGoal,
        contentMixPreset: '80_20' as ContentMixPreset,
        sourceData: {},
        sourceDataIds: { audienceIds: [], taskIds: [] },
        autoFillBacklog: false,
        autoAssignTopicsToSlots: true,
        generateFirstSprintPosts: true,
        firstSprintApplyMode: options.reviewDrafts ? 'draft_review' : 'auto_apply_sprint',
      });
      if (!response.success) throw new Error(response.error ?? 'Strategy creation failed to start');
      patchStage('strategy', { status: 'running', taskId: response.taskId });
    } catch (e) {
      patchStage('strategy', { status: 'error', error: (e as Error).message });
    }
  }, [apiClient, authorId, options.reviewDrafts, patchStage, platforms, projectId, userId]);

  const autoApplyGeneration = useCallback(async (
    entity: 'topic' | 'post',
    generationId: string,
    sprintId?: string,
  ) => {
    const db = CoreFirebaseService.getInstance().firestore;
    const runCollection = entity === 'topic' ? 'topic_generations' : 'post_generations';
    const draftsCollection = entity === 'topic' ? 'generatedTopics' : 'generatedPosts';
    const snap = await getDocs(
      collection(db, 'projects', projectId, runCollection, generationId, draftsCollection),
    );
    const ids = snap.docs.map((d) => d.id);
    if (ids.length === 0) throw new Error(`Generation ${generationId} produced no drafts to apply.`);
    if (entity === 'topic') {
      const result = await apiClient.applyTopics({
        projectId, authorId: authorId ?? '', generationId, acceptedItemIds: ids,
      });
      if (!result.success) throw new Error((result as { error?: string }).error ?? 'Auto-apply failed');
    } else {
      const result = await apiClient.applyPosts({
        projectId, authorId: authorId ?? '', generationId, sprintId: sprintId ?? '', acceptedItemIds: ids,
      });
      if (!result.success) throw new Error((result as { error?: string }).error ?? 'Auto-apply failed');
    }
  }, [apiClient, authorId, projectId]);

  // ── Stage entry: readiness-first checks, then launch/pause/skip ────

  const enterStage = useCallback((stageId: RunAllStageId) => {
    switch (stageId) {
      case 'author': {
        if (counts.authorResolved) {
          patchStage('author', { status: 'skipped', note: 'Author already exists.' });
          return;
        }
        patchStage('author', {
          status: 'paused',
          pauseKind: 'needs-author',
          note: 'Author onboarding needs your profile input.',
        });
        return;
      }
      case 'pillars': {
        if (counts.confirmedPillarCount > 0) {
          patchStage('pillars', {
            status: 'skipped',
            note: `${counts.confirmedPillarCount} confirmed pillar(s) already exist.`,
          });
          return;
        }
        if (options.confirmPillarsManually) {
          patchStage('pillars', {
            status: 'paused',
            pauseKind: 'confirm-pillars',
            note: counts.pillarCount > 0
              ? `${counts.pillarCount} draft pillar(s) await your confirmation.`
              : 'No pillars yet — generate them from the Creator sub-tab, then confirm.',
          });
          return;
        }
        // Auto path (toggle OFF): confirm existing drafts, else generate with
        // autoConfirmPillars:true via the analyze endpoint.
        if (draftPillars.length > 0) {
          patchStage('pillars', { status: 'running' });
          void (async () => {
            try {
              await Promise.all(draftPillars.map((pillar) => pillarService.confirm(projectId, pillar.id)));
              // Completion detected by the counts watcher below.
            } catch (e) {
              patchStage('pillars', { status: 'error', error: (e as Error).message });
            }
          })();
          return;
        }
        void startPillarsAutoGeneration();
        return;
      }
      case 'topics': {
        if (counts.readyTopicCount >= CONTENT_PLAN_BACKLOG_READY_THRESHOLD) {
          patchStage('topics', {
            status: 'skipped',
            note: `${counts.readyTopicCount} ready topics already in the backlog.`,
          });
          return;
        }
        void startTopicsGeneration();
        return;
      }
      case 'strategy': {
        if (counts.strategyCount > 0) {
          patchStage('strategy', { status: 'skipped', note: 'A strategy already exists.' });
          return;
        }
        void startStrategyCreation();
        return;
      }
      case 'posts': {
        if (counts.postCount > 0) {
          patchStage('posts', { status: 'skipped', note: 'Posts already exist.' });
          return;
        }
        const strategyStage = stages.find((s) => s.id === 'strategy');
        const chainedTaskId = strategyStage?.note?.startsWith('chained:')
          ? strategyStage.note.slice('chained:'.length)
          : undefined;
        if (chainedTaskId) {
          patchStage('posts', { status: 'running', taskId: chainedTaskId, generationId: chainedTaskId });
          return;
        }
        patchStage('posts', {
          status: 'paused',
          pauseKind: 'review-posts',
          note: 'Generate posts from the strategy detail (Open Strategies → Generate posts).',
        });
        return;
      }
      default:
        return;
    }
  }, [
    counts, options.confirmPillarsManually, draftPillars, patchStage, pillarService,
    projectId, stages, startPillarsAutoGeneration, startStrategyCreation, startTopicsGeneration,
  ]);

  // ── Driver: advance pending stages sequentially ────────────────────
  useEffect(() => {
    if (!active) return;
    const next = stages.find((s) => s.status !== 'done' && s.status !== 'skipped');
    if (!next) {
      setActive(false);
      startedStageRef.current = null;
      return;
    }
    if (next.status === 'pending' && startedStageRef.current !== next.id) {
      startedStageRef.current = next.id;
      enterStage(next.id);
    }
  }, [active, stages, enterStage]);

  // ── Watchers: readiness-driven completion of paused/running stages ─
  useEffect(() => {
    if (!active) return;
    setStages((prev) => prev.map((stage) => {
      if (stage.id === 'author' && stage.status === 'paused' && counts.authorResolved) {
        startedStageRef.current = null;
        return { ...stage, status: 'done', pauseKind: undefined, note: 'Author created.' };
      }
      if (stage.id === 'pillars'
        && (stage.status === 'paused' || (stage.status === 'running' && !stage.taskId))
        && counts.confirmedPillarCount > 0) {
        startedStageRef.current = null;
        return {
          ...stage,
          status: 'done',
          pauseKind: undefined,
          note: `${counts.confirmedPillarCount} pillar(s) confirmed.`,
        };
      }
      if (stage.id === 'topics' && stage.status === 'paused'
        && counts.readyTopicCount >= CONTENT_PLAN_BACKLOG_READY_THRESHOLD) {
        startedStageRef.current = null;
        return {
          ...stage,
          status: 'done',
          pauseKind: undefined,
          note: `${counts.readyTopicCount} ready topics in the backlog.`,
        };
      }
      if (stage.id === 'posts' && (stage.status === 'paused' || stage.status === 'running')
        && counts.postCount > 0) {
        startedStageRef.current = null;
        return { ...stage, status: 'done', pauseKind: undefined, note: `${counts.postCount} post(s) applied.` };
      }
      return stage;
    }));
  }, [active, counts.authorResolved, counts.confirmedPillarCount, counts.readyTopicCount, counts.postCount]);

  // ── Watcher: backend task terminal states for the running stage ────
  useEffect(() => {
    if (!active || !runningTaskId || !runningTask.status) return;
    const stage = stages.find((s) => s.status === 'running' && s.taskId === runningTaskId);
    if (!stage) return;

    if (runningTask.status === 'error') {
      patchStage(stage.id, {
        status: 'error',
        error: runningTask.error ?? 'Backend task failed without an error message.',
      });
      return;
    }
    if (runningTask.status !== 'completed') return;

    if (stage.id === 'pillars') {
      // autoConfirmPillars ran server-side; the counts watcher flips the stage
      // to done once confirmed pillars appear. If none were confident enough,
      // fall back to an explicit confirm checkpoint.
      patchStage('pillars', {
        status: 'paused',
        pauseKind: 'confirm-pillars',
        taskId: undefined,
        note: 'Pillar generation finished — confirm the drafts that were not auto-confirmed.',
      });
      return;
    }
    if (stage.id === 'topics') {
      if (options.reviewDrafts) {
        patchStage('topics', {
          status: 'paused',
          pauseKind: 'review-topics',
          taskId: undefined,
          note: 'Topic drafts are ready for review.',
        });
      } else {
        patchStage('topics', { status: 'running', taskId: undefined, note: 'Auto-applying drafts…' });
        void (async () => {
          try {
            await autoApplyGeneration('topic', stage.generationId ?? runningTaskId);
            // Counts watcher completes the stage when the threshold is met;
            // if one batch was not enough, surface the review pause.
            patchStage('topics', {
              status: 'paused',
              pauseKind: 'review-topics',
              note: 'Drafts applied — waiting for the ready-topic threshold.',
            });
          } catch (e) {
            patchStage('topics', { status: 'error', error: (e as Error).message });
          }
        })();
      }
      return;
    }
    if (stage.id === 'strategy') {
      const result = (runningTask.task as { result?: Record<string, unknown> } | null)?.result;
      const chainedTaskId = typeof result?.chainedFirstSprintTaskId === 'string'
        ? result.chainedFirstSprintTaskId
        : undefined;
      startedStageRef.current = null;
      patchStage('strategy', {
        status: 'done',
        taskId: undefined,
        // The posts stage reads the chained task id from this note (kept out
        // of user-visible copy by the panel, which renders its own line).
        note: chainedTaskId ? `chained:${chainedTaskId}` : 'Strategy created.',
      });
      return;
    }
    if (stage.id === 'posts') {
      if (options.reviewDrafts) {
        void (async () => {
          let sprintId: string | undefined;
          try {
            const strategyService = new ContentStrategyFirestoreService();
            const strategyId = (runningTask.task as { strategyId?: string } | null)?.strategyId
              ?? runningTask.strategyId;
            if (strategyId) {
              const sprints = await strategyService.listSprints(projectId, strategyId);
              sprintId = sprints[0]?.id;
            }
          } catch {
            // sprint id is only needed for the inline review-apply; the pause
            // row still offers the Strategies sub-tab as a fallback.
          }
          patchStage('posts', {
            status: 'paused',
            pauseKind: 'review-posts',
            taskId: undefined,
            sprintId,
            note: 'Post drafts are ready for review.',
          });
        })();
      } else {
        // auto_apply_sprint applied posts server-side; counts watcher closes it.
        patchStage('posts', { status: 'running', taskId: undefined, note: 'Waiting for applied posts…' });
      }
    }
  }, [
    active, runningTaskId, runningTask.status, runningTask.error, runningTask.task,
    runningTask.strategyId, stages, options.reviewDrafts, patchStage, autoApplyGeneration, projectId,
  ]);

  // ── Public API ─────────────────────────────────────────────────────

  const start = useCallback((nextOptions: RunAllOptions) => {
    setOptions(nextOptions);
    setStages(initialStages());
    setReviewTarget(null);
    startedStageRef.current = null;
    setActive(true);
  }, []);

  const stop = useCallback(() => {
    setActive(false);
    startedStageRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    setActive(false);
    setStages(initialStages());
    setReviewTarget(null);
    startedStageRef.current = null;
  }, []);

  const confirmAllDraftPillars = useCallback(async () => {
    await Promise.all(draftPillars.map((pillar) => pillarService.confirm(projectId, pillar.id)));
  }, [draftPillars, pillarService, projectId]);

  const retryStage = useCallback((stageId: RunAllStageId) => {
    startedStageRef.current = null;
    patchStage(stageId, {
      status: 'pending', error: undefined, taskId: undefined, pauseKind: undefined,
    });
    if (!active) setActive(true);
  }, [active, patchStage]);

  const openReview = useCallback((stage: RunAllStageState) => {
    if (!stage.generationId) return;
    setReviewTarget({
      entity: stage.id === 'topics' ? 'topic' : 'post',
      generationId: stage.generationId,
      sprintId: stage.sprintId,
    });
  }, []);

  const closeReview = useCallback(() => setReviewTarget(null), []);

  const applyReview = useCallback(async (acceptedItemIds: string[]) => {
    if (!reviewTarget) return;
    if (reviewTarget.entity === 'topic') {
      const result = await apiClient.applyTopics({
        projectId,
        authorId: authorId ?? '',
        generationId: reviewTarget.generationId,
        acceptedItemIds,
      });
      if (!result.success) throw new Error((result as { error?: string }).error ?? 'Apply failed');
    } else {
      const result = await apiClient.applyPosts({
        projectId,
        authorId: authorId ?? '',
        generationId: reviewTarget.generationId,
        sprintId: reviewTarget.sprintId ?? '',
        acceptedItemIds,
      });
      if (!result.success) throw new Error((result as { error?: string }).error ?? 'Apply failed');
    }
  }, [apiClient, authorId, projectId, reviewTarget]);

  // The onboarding pause action is surfaced by the panel; expose it through
  // the stage note so the panel can call the page callback directly.
  void onOpenOnboarding;

  return {
    active,
    options,
    stages,
    currentPhaseName: runningTask.currentPhaseName,
    start,
    stop,
    dismiss,
    confirmAllDraftPillars,
    retryStage,
    reviewTarget,
    openReview,
    closeReview,
    applyReview,
  };
}
